/**
 * useAiBuddySettings — memory and suggestion preferences for the AI buddy the
 * member follows (GET/POST /Agent/AiBuddySettings).
 *
 * The agent id comes from the followed buddy on the cached profile, so the
 * screen has an explicit "no buddy yet" state instead of guessing an agent.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import aiBuddyService, {
  type AiBuddySettings,
} from '../api/services/aiBuddyService';
import messageService from '../api/services/messageService';
import { useProfile } from '../context/ProfileContext';
import { getApiErrorMessage } from '../utils/apiErrorHandler';
import type { ApiError } from '../types/api';

const DEFAULT_SETTINGS: AiBuddySettings = {
  keepHistoryFor30Days: true,
  enableSuggestLikes: true,
  enableSuggestComments: true,
};

export interface AiBuddySettingsSaveResult {
  success: boolean;
  message?: string;
}

export interface UseAiBuddySettingsResult {
  settings: AiBuddySettings;
  buddyName: string;
  hasAiBuddy: boolean;
  loading: boolean;
  saving: boolean;
  error: string;
  hasChanges: boolean;
  setField: <K extends keyof AiBuddySettings>(field: K, value: boolean) => void;
  reload: () => void;
  save: () => Promise<AiBuddySettingsSaveResult>;
}

export const useAiBuddySettings = (): UseAiBuddySettingsResult => {
  const { profile } = useProfile();
  const agent = profile?.followedAiAgent ?? null;
  const agentUserId = Number(agent?.agentId);
  const hasAiBuddy = Number.isSafeInteger(agentUserId) && agentUserId > 0;

  const [settings, setSettings] = useState<AiBuddySettings>(DEFAULT_SETTINGS);
  const [savedSettings, setSavedSettings] = useState<AiBuddySettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  const load = useCallback(async () => {
    if (!hasAiBuddy) return;
    setLoading(true);
    setError('');
    try {
      const response = await aiBuddyService.getAiBuddySettings(agentUserId);
      const loaded = response.data ?? DEFAULT_SETTINGS;
      setSettings(loaded);
      setSavedSettings(loaded);
    } catch (caught) {
      setError(getApiErrorMessage(caught as ApiError));
    } finally {
      setLoading(false);
    }
  }, [agentUserId, hasAiBuddy]);

  useEffect(() => {
    load().catch(() => undefined);
  }, [load, reloadToken]);

  const setField = useCallback(
    <K extends keyof AiBuddySettings>(field: K, value: boolean) => {
      setSettings(previous => ({ ...previous, [field]: value }));
    },
    [],
  );

  const hasChanges = useMemo(
    () =>
      (Object.keys(settings) as Array<keyof AiBuddySettings>).some(
        field => settings[field] !== savedSettings[field],
      ),
    [savedSettings, settings],
  );

  const save = useCallback(async (): Promise<AiBuddySettingsSaveResult> => {
    if (!hasAiBuddy) return { success: false };
    setSaving(true);
    try {
      await aiBuddyService.saveAiBuddySettings({ agentUserId, ...settings });
      setSavedSettings(settings);

      // Refresh/reset the latest Conversation data if followedAiAgent has a valid conversationId
      const conversationId = agent?.conversationId;
      if (agent !== null && conversationId != null && Number(conversationId) > 0) {
        try {
          await messageService.getConversationById(conversationId, profile?.userId);
        } catch (convErr) {
          console.error('Failed to refresh conversation after AI settings update:', convErr);
        }
      }

      return { success: true };
    } catch (caught) {
      return { success: false, message: getApiErrorMessage(caught as ApiError) };
    } finally {
      setSaving(false);
    }
  }, [agent, agentUserId, hasAiBuddy, profile?.userId, settings]);

  const reload = useCallback(() => setReloadToken(token => token + 1), []);

  return {
    settings,
    buddyName: agent?.userName ?? '',
    hasAiBuddy,
    loading,
    saving,
    error,
    hasChanges,
    setField,
    reload,
    save,
  };
};

export default useAiBuddySettings;
